import React, { useEffect, useState } from "react"
import { FormLabel } from "@wfp/ui"
import Select, { Options } from "react-select"
import { FormFrequencyData, SelectOption } from "interfaces/dataInterfaces"
import { dayOptions, FrequencyMode, frequencyOptions, weekOptions } from "constants/household"

type ScheduleFrequencyProps = { onFrequencyComplete: (params: FormFrequencyData) => void }
type TimeComponentProps = {
  label: string
  value?: string
  id: string
}
type SelectComponentProps<T> = {
  label: string
  id: string
  options: Options<T> | undefined
}

export const initialFormFrequency: FormFrequencyData = {
  frequencyId: 0,
  time: "",
  weekDay: null,
  secondTime: "",
  secondWeekDay: null,
  monthDay: 0,
}

function ScheduleFrequency({ onFrequencyComplete }: ScheduleFrequencyProps) {
  const [data, setData] = useState<FormFrequencyData>(initialFormFrequency)

  // eslint-disable-next-line consistent-return
  const validation = () => {
    if (!(data.frequencyId in FrequencyMode)) return true

    // eslint-disable-next-line default-case
    switch (data.frequencyId) {
      case FrequencyMode.OnceADay:
        return !!data.time
      case FrequencyMode.TwiceADay:
        return !!data.time && !!data.secondTime
      case FrequencyMode.OnceAWeek:
        return !!data.time && !!data.weekDay
      case FrequencyMode.TwiceAWeek:
        return !!data.time && !!data.secondTime && !!data.weekDay && !!data.secondWeekDay
      case FrequencyMode.OnceAMonth:
        return !!data.time && !!data.monthDay
    }
  }

  const handleSubmit = () => {
    const validate = validation()
    validate ? onFrequencyComplete(data) : onFrequencyComplete(initialFormFrequency)
  }

  useEffect(() => {
    return () => onFrequencyComplete(initialFormFrequency)
  }, [])

  useEffect(() => {
    data.frequencyId && handleSubmit()
  }, [data])

  function RenderSelectComponent({ label, id, options }: SelectComponentProps<SelectOption>) {
    return (
      <div>
        <FormLabel htmlFor={id}>{label}</FormLabel>
        <Select
          inputId={id}
          placeholder={label}
          options={options ?? []}
          value={options?.find(o => o.value === data[id]) ?? null}
          onChange={e => e && setData({ ...data, [id]: e.value })}
          menuPlacement="top"
        />
      </div>
    )
  }

  function RenderTimeComponent({ label, value, id }: TimeComponentProps) {
    return (
      <div>
        <FormLabel htmlFor={id}>{label}</FormLabel>
        <div>
          <input
            type="time"
            id={id}
            required
            onChange={e => setData({ ...data, [id]: e.target.value })}
            value={value}
            style={{ height: "38px", border: "1px solid #ccc", borderRadius: "5px" }}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 mt-2">
        <div>
          <FormLabel htmlFor="frequency" className="text-xl">
            Schedule frequency
          </FormLabel>
          <Select
            inputId="frequency"
            placeholder="Select a frequency"
            options={frequencyOptions}
            value={frequencyOptions.find(f => f.value === data.frequencyId) ?? null}
            onChange={e => e && setData({ ...initialFormFrequency, frequencyId: e.value })}
            menuPlacement="top"
          />
        </div>
      </div>

      {data.frequencyId in FrequencyMode && (
        <>
          <div className="grid gap-2 mt-2 w-50" style={{ gridTemplateColumns: "2fr 5fr 2fr" }}>
            <RenderTimeComponent label="Time" id="time" value={data.time} />

            {(data.frequencyId === FrequencyMode.OnceAWeek || data.frequencyId === FrequencyMode.TwiceAWeek) && (
              <RenderSelectComponent label="Week day" id="weekDay" options={weekOptions} />
            )}

            {data.frequencyId === FrequencyMode.OnceAMonth && (
              <RenderSelectComponent label="Month day" id="monthDay" options={dayOptions} />
            )}
          </div>

          {(data.frequencyId === FrequencyMode.TwiceADay || data.frequencyId === FrequencyMode.TwiceAWeek) && (
            <div className="grid gap-2 mt-2 w-50" style={{ gridTemplateColumns: "2fr 5fr 2fr" }}>
              <RenderTimeComponent label="Second time" id="secondTime" value={data.secondTime} />

              {data.frequencyId === FrequencyMode.TwiceAWeek && (
                <RenderSelectComponent label="Second week day" id="secondWeekDay" options={weekOptions} />
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}

export default ScheduleFrequency
